import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { fetchHats } from "@/lib/api";

const CRAFT = [
  {
    n: "01",
    title: "Honest Materials",
    body: "Scottish tweeds, Blackwatch and Honey tartans, British waxed cotton — cloth chosen to outlast trends and weather alike.",
  },
  {
    n: "02",
    title: "Traditional Methods",
    body: "Cut, sewn and finished the way it has been done for generations. No shortcuts, no compromises.",
  },
  {
    n: "03",
    title: "Skilled Hands",
    body: "Every hat passes through practised hands before it earns the Capstone name. Detail is not a feature — it is the point.",
  },
];

export default function Home() {
  const [hats, setHats] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    fetchHats()
      .then((data) => setHats(data.filter((h) => h.featured)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative pt-20 min-h-screen flex flex-col justify-center overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-12 gap-10 items-center py-20">
          <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
            <Reveal delay={0.1} y={16}>
              <Eyebrow className="mb-8">Est. United Kingdom — British Heritage Hatmaker</Eyebrow>
            </Reveal>
            <h1 className="font-serif text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.25rem] leading-[1.04] tracking-tight" data-testid="hero-headline">
              <MaskedLine delay={0.25}>Welcome to</MaskedLine>
              <MaskedLine delay={0.38}>
                Capstone <span className="italic text-green">Hats</span> LTD
              </MaskedLine>
            </h1>
            <MaskedLine delay={0.55} className="mt-8">
              <span className="font-serif italic text-xl md:text-2xl text-smoke">
                Discover Our Exquisite Hat Collection.
              </span>
            </MaskedLine>
            <Reveal delay={0.7} className="mt-8 max-w-md">
              <p className="text-smoke leading-relaxed text-[0.9375rem]">
                Premium tweed and wax cotton hats, made in the United Kingdom
                using traditional methods. Tradition in every stitch.
              </p>
            </Reveal>
            <Reveal delay={0.85} className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/gallery"
                data-testid="hero-gallery-cta"
                className="group inline-flex items-center gap-3 bg-green text-ivory eyebrow !text-[0.625rem] px-8 py-5 hover:bg-ink transition-colors duration-300"
              >
                Explore the collection
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/wholesale"
                data-testid="hero-wholesale-cta"
                className="group inline-flex items-center gap-3 border border-ink eyebrow !text-[0.625rem] px-8 py-5 hover:bg-ink hover:text-ivory transition-colors duration-300"
              >
                Wholesale enquiries
                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
            </Reveal>
          </motion.div>

          <motion.div style={{ y: imgY }} className="lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <img
                src="/images/hero.png"
                alt="A Capstone tweed flat cap resting on folded tartan"
                data-testid="hero-image"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-ivory border border-line px-6 py-4">
                <p className="eyebrow !text-[0.6rem] text-honey">No. 001 — The Collection</p>
                <p className="font-serif italic mt-1 text-sm">From the factory floor</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* STORY TEASER */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-3">
          <Reveal>
            <p className="font-serif text-7xl text-line select-none">01</p>
            <Eyebrow className="mt-4">The Brand</Eyebrow>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.15] tracking-tight" data-testid="story-headline">
              At Capstone, we blend tradition into{" "}
              <span className="italic text-green">every stitch.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 space-y-6 max-w-xl">
            <p className="text-smoke leading-relaxed">
              We are reviving the pride of British manufacturing, one hat at a
              time. From Blackwatch to Honey tartan, every cloth we cut carries
              centuries of Scottish weaving — and every cap that leaves the
              atelier is built to be worn daily and kept for decades.
            </p>
            <p className="text-smoke leading-relaxed">
              Authenticity, durability and detail are not marketing words here.
              They are the measure of every seam.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="mt-10">
            <Link
              to="/about"
              data-testid="story-about-link"
              className="nav-link eyebrow !text-[0.625rem] text-ink inline-flex items-center gap-2"
            >
              Read our story <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-3 flex items-end">
          <Reveal delay={0.25} className="w-full">
            <img
              src="/images/tartan-texture.png"
              alt="Honey tartan weave in detail"
              data-testid="story-texture-image"
              className="w-full aspect-square object-cover"
            />
            <p className="eyebrow !text-[0.6rem] text-smoke mt-4">
              Honey tartan — detail
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED HATS */}
      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <Reveal><Eyebrow className="mb-5">The Collection — Selected</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-4xl md:text-6xl tracking-tight" data-testid="featured-headline">
                  Featured pieces
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/gallery"
                data-testid="featured-view-all"
                className="nav-link eyebrow !text-[0.625rem] inline-flex items-center gap-2"
              >
                View the full collection <ArrowRight size={13} />
              </Link>
            </Reveal>
          </div>

          <div className="divide-y divide-line border-y border-line">
            {hats.map((hat, i) => (
              <Reveal key={hat.slug} delay={i * 0.08}>
                <Link
                  to="/gallery"
                  data-testid={`featured-hat-${hat.slug}`}
                  className="group grid md:grid-cols-12 gap-6 items-center py-10 md:py-12"
                >
                  <p className="md:col-span-1 font-serif text-2xl text-line group-hover:text-honey transition-colors duration-500">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <div className="md:col-span-5">
                    <h3 className="font-serif text-2xl md:text-4xl tracking-tight group-hover:italic transition-all duration-500">
                      {hat.name}
                    </h3>
                    <p className="eyebrow !text-[0.6rem] text-smoke mt-3">{hat.materials}</p>
                  </div>
                  <p className="md:col-span-4 text-sm text-smoke leading-relaxed hidden md:block">
                    {hat.note}
                  </p>
                  <div className="md:col-span-2 overflow-hidden">
                    <img
                      src={hat.image}
                      alt={hat.name}
                      loading="lazy"
                      className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CRAFTSMANSHIP */}
      <section className="bg-green text-ivory">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow className="!text-honey mb-6">Craftsmanship</Eyebrow>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.12] tracking-tight" data-testid="craft-headline">
                Made the slow way, <span className="italic">on purpose.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="mt-12">
              <img
                src="/images/factory.jpg"
                alt="Inside the Capstone sewing factory"
                data-testid="craft-factory-image"
                className="w-full aspect-[4/3] object-cover"
              />
              <p className="eyebrow !text-[0.6rem] text-ivory/50 mt-4">The factory floor — United Kingdom</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center divide-y divide-ivory/15">
            {CRAFT.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1} className="py-10 first:pt-0 last:pb-0">
                <div className="grid sm:grid-cols-12 gap-4 items-baseline">
                  <p className="sm:col-span-2 font-serif text-3xl text-honey">{c.n}</p>
                  <h3 className="sm:col-span-4 font-serif text-2xl">{c.title}</h3>
                  <p className="sm:col-span-6 text-sm text-ivory/70 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <Reveal>
          <Eyebrow className="mb-8">Continue</Eyebrow>
        </Reveal>
        <div className="grid md:grid-cols-2 border border-line divide-y md:divide-y-0 md:divide-x divide-line">
          <Reveal delay={0.1}>
            <Link
              to="/gallery"
              data-testid="cta-gallery"
              className="group flex items-center justify-between p-10 md:p-16 hover:bg-cream transition-colors duration-500"
            >
              <div>
                <p className="eyebrow !text-[0.6rem] text-smoke mb-4">One factory, one standard</p>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight">Visit the gallery</h3>
              </div>
              <ArrowRight size={28} strokeWidth={1.25} className="text-honey group-hover:translate-x-2 transition-transform duration-500" />
            </Link>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/wholesale"
              data-testid="cta-wholesale"
              className="group flex items-center justify-between p-10 md:p-16 hover:bg-cream transition-colors duration-500"
            >
              <div>
                <p className="eyebrow !text-[0.6rem] text-smoke mb-4">Stock Capstone in your shop</p>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight">Wholesale services</h3>
              </div>
              <ArrowRight size={28} strokeWidth={1.25} className="text-honey group-hover:translate-x-2 transition-transform duration-500" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
